import { get, post } from "Service";

const initiatePayment = (payload: any) => {
  const request = {
    subUrl: `initiate`,
    data: payload,
    isPayment: true,
  };
  return post(request);
};

const getAdminPaymentHistory = async (pageNumber: number = 1, pageSize: number = 10, searchQuery: string = "") => {
  try {
    const request = {
      subUrl: `Payments/admin?pageNumber=${pageNumber}&pageSize=${pageSize}&searchQuery=${encodeURIComponent(searchQuery)}`,
      isAuth: false,
    };
    return await get(request);
  } catch (error) {
    console.error("Error fetching order history:", error);
    throw error;
  }
}

const getUserPaymentHistory = async (pageNumber: number = 1, pageSize: number = 10, searchQuery: string = "") => {
  try {
    const request = {
      subUrl: `Payments/user-payments?pageNumber=${pageNumber}&pageSize=${pageSize}&searchQuery=${encodeURIComponent(searchQuery)}`,
      isAuth: false,
    };
    return await get(request);
  } catch (error) {
    console.error("Error fetching order history:", error);
    throw error;
  }
}

const getMergedHistory = async (userId?: number, pageNumber: number = 1, pageSize: number = 10000, searchQuery: string = "", paymentStatusFilter: string = "All", orderType: number = 0) => {
  try {
    const url = `Payments/merged-history?userId=${userId || 0}&orderType=${orderType}&pageNumber=${pageNumber}&pageSize=${pageSize}&searchQuery=${encodeURIComponent(searchQuery)}&paymentStatusFilter=${encodeURIComponent(paymentStatusFilter)}`;
    const request = {
      subUrl: url,
      isAuth: false,
    };
    return await get(request);
  } catch (error) {
    console.error("Error fetching merged history:", error);
    throw error;
  }
}

const refundPayment = (payload: any) => {
  const request = {
    subUrl: `Payments/refund`,
    data: payload,
    isAuth: false,
  };
  return post(request);
};

const getRewardHistory = (userId: number, pageNumber: number = 1, pageSize: number = 10) => {
  const request = {
    subUrl: `Payments/Rewards/user-history/${userId}?pageNumber=${pageNumber}&pageSize=${pageSize}`,
    data: {},
    isAuth: false,
  };
  return get(request);
};


/**
 * GET: Fetch cases assigned to a specific professional that are finished/completed 
 * but do not yet have an associated payout record.
 * @param {string | number} profId - The Unique ID of the Professional.
 */
export const getProfessionalUnpaidCases = (profId: string | number) => {
  const request = {
    subUrl: `Payments/cases-eligible-for-payout/${profId}`,
    isAuth: false,
  };
  return get(request); // Assuming you have a get() helper similar to put()
};

/**
 * POST: Allows an Admin to submit a batch of payment amounts for various cases.
 * These records are saved in the 'Payouts' table with a 'PendingApproval' status.
 * @param {Object} payload - { professionalId: number, entries: [{ caseId: number, amount: number }] }
 */
export const submitBulkProfessionalPayments = (payload: any) => {
  const request = {
    subUrl: "Payments/request-payouts",
    isAuth: false,
    data: payload,
  };
  return post(request);
};

/**
 * GET: Fetches a list of all payout records currently waiting for SuperAdmin approval.
 * Used to populate the SuperAdminApproval queue table.
 */
export const getPendingPayouts = (type?: string) => {
  const url = type ? `Payments/pending-payouts?type=${type}` : "Payments/pending-payouts";
  return get({ subUrl: url, isAuth: false });
};

/**
 * POST: Finalizes payouts by changing status from 'PendingApproval' to 'Completed'.
 * This updates both the Payout record and the corresponding Case payment status.
 * Supports both single approval (array of 1 ID) and bulk approval (array of many IDs).
 * @param {number[]} payoutIds - An array of Payout Primary Keys (e.g., [101, 102, 105]).
 */
export const approvePayoutsBulk = (payoutIds: number[]) => {
  return post({
    subUrl: `Payments/approve-payouts-bulk`,
    isAuth: false,
    data: { payoutIds }
  });
};

export const downloadCompletedPayouts = () => {
  const request = {
    subUrl: `Payments/export-completed-payouts`,
    isAuth: false,
    excel: true,
  };
  return get(request);
};

/**
 * GET: Fetches a list of all completed payout records.
 * Used to populate the CompletedPayouts table.
 */
export const getCompletedPayouts = (type?: string) => {
  const url = type ? `Payments/completed-payouts?type=${type}` : "Payments/completed-payouts";
  return get({ subUrl: url, isAuth: false });
};

/**
 * POST: Generates a payment link for a specific case.
 * @param {string | number} profId - The Unique ID of the Professional.
 * @param {Object} payload - { caseId: number, amount: number }
 */
export const generatePaymentLinkForCase = (payload: any) => {
  const request = {
    subUrl: `Payments/sent-payment-link-for-case`,
    isAuth: false,
    data: payload,
  };
  return post(request);
};

/**
 * GET: Fetches a list of all reward redeem requests.
 * Used to populate the RewardRedeemRequests table.
 */
export const getAllRedeemRequests = () => {
  return get({ subUrl: "Payments/reward-redeem-requests", isAuth: false });
};

/**
 * POST: Verifies a reward redeem request.
 * @param {number} requestId - The Primary Key of the RewardRedeemRequest record.
 */
export const verifyRedemption = (requestId: number) => {
  return post({
    subUrl: `Payments/verify-redeem-request`,
    isAuth: false,
    data: { requestId }
  });
};

/**
 * POST: Submits a reward redeem request.
 * @param {Object} payload - { userId: number, points: number }
 */
export const postRedemption = (payload: any) => {
  return post({
    subUrl: `Payments/submit-redeem-request`,
    isAuth: false,
    data: payload
  });
};

/**
 * GET: Fetches the current reward balance for a specific user.
 * @param {number} userId - The Unique ID of the User.
 */
export const getUserRewardBalance = (userId: number) => {
  return get({
    subUrl: `Payments/Rewards/balance/${userId}`,
    isAuth: false
  });
};

export const getAllUsersRewards = (pageNumber: number = 1, pageSize: number = 10, searchQuery: string = "") => {
  return get({
    subUrl: `Payments/Rewards/all-users?pageNumber=${pageNumber}&pageSize=${pageSize}&searchQuery=${encodeURIComponent(searchQuery)}`,
    isAuth: false
  });
};

export const getPaymentsByCaseId = (caseId: number) => {
  return get({
    subUrl: `Payments/by-case/${caseId}`,
    isAuth: false
  });
};



const PaymentApi = {
  initiatePayment,
  getAdminPaymentHistory,
  getUserPaymentHistory,
  getMergedHistory,
  refundPayment,
  getRewardHistory,
  getProfessionalUnpaidCases,
  submitBulkProfessionalPayments,
  getPendingPayouts,
  approvePayoutsBulk,
  getCompletedPayouts,
  downloadCompletedPayouts,
  generatePaymentLinkForCase,
  getAllRedeemRequests,
  verifyRedemption,
  postRedemption,
  getUserRewardBalance,
  getAllUsersRewards,
  getPaymentsByCaseId,
};

export default PaymentApi;
